import { Box, Skeleton, Switch, Tooltip, Typography } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useStyles } from './style'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { BannerStatic } from '../../../components'
import { TenantEditSlice } from '../../../reducers'
import { useDispatch } from 'react-redux'
import { SelfRegistrationDisabledModal } from './modals/disable-modal'
import { selfRegistrationModalKeys } from '.'

export const SelfRegistrationToggle = ({ tenant, useModal = false }) => {
  const classes = useStyles()
  const [disabledModal, setDisabledModal] = useState(false)

  const loginProvider = useMemo(() => {
    return tenant.loginProviders.find((lp) => lp.providerType === 'USERNAME')
  }, [tenant])

  const selfRegistrationIsDisabled = useMemo(() => {
    if (!loginProvider) return true

    return loginProvider.selfRegistrationDomains.length === 0
  }, [loginProvider])

  useEffect(() => {
    setValue(loginProvider?.selfRegistration || false)
  }, [loginProvider])

  const [value, setValue] = useState(false)
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)

  const [tenantUpdate] = useEvent([
    {
      eventName: 'tenantUpdate',
      onSuccess: (tenant) => {
        BannerStatic.show({
          label: 'Self registration status updated successfully.'
        })
        setLoading(false)
        setShowTooltip(false)
        dispatch(
          TenantEditSlice.actions.updateTenantLoginProviders(
            tenant.loginProviders
          )
        )
      },
      onError: () => {
        BannerStatic.show({
          label: 'There was an error updating the self registration status.',
          isError: true
        })
        setLoading(false)
        setShowTooltip(false)
        setValue((current) => !current)
      }
    }
  ])

  const handleToggle = async (event) => {
    const input = event.target.checked
    const hideModal =
      localStorage.getItem(selfRegistrationModalKeys.disabled) === 'no'

    if (!input && useModal && !hideModal) {
      setDisabledModal(true)
    } else {
      await updateSelfRegistrationStatus(input)
    }
  }

  const updateSelfRegistrationStatus = async (status) => {
    setValue(status)
    setLoading(true)

    await tenantUpdate.trigger({
      pk: tenant.pk,
      selfRegistration: status
    })
  }

  const statusMessage = useMemo(() => {
    if (loading) {
      return <Skeleton variant='rectangular' width={210} />
    }

    return value ? (
      <Typography data-test='self-registration-status-message'>
        Self registration is <strong>enabled</strong>.
      </Typography>
    ) : (
      <Typography data-test='self-registration-status-message'>
        Self registration is <strong>disabled</strong>.
      </Typography>
    )
  }, [loading, value])

  return (
    <>
      {selfRegistrationIsDisabled ? (
        <Typography
          variant='caption'
          className={classes.subtitle}
          data-test='self-registration-status-message'
        >
          Define the email domains that will be allowed to self register.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex' }}>
          {statusMessage}
          <Box className={classes.switchContainer}>
            <Tooltip
              open={showTooltip}
              onOpen={() => setShowTooltip(true)}
              onClose={() => setShowTooltip(false)}
              title={
                value
                  ? 'Temporarily deactivate self-registration.'
                  : 'Activate self-registration.'
              }
              placement='top'
            >
              <Switch
                checked={value}
                size='small'
                disabled={loading}
                onChange={handleToggle}
              />
            </Tooltip>
          </Box>
        </Box>
      )}

      <SelfRegistrationDisabledModal
        open={disabledModal}
        handleProceed={updateSelfRegistrationStatus}
        handleClose={() => setDisabledModal((state) => !state)}
        id={selfRegistrationModalKeys.disabled}
      />
    </>
  )
}
